import React, { useState } from 'react';
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';

import { useTranslation } from 'react-i18next';
import { AuthControllerService } from '../api/services/AuthControllerService';
import { useToast } from '../hooks/useToast';

export default function ForgotPasswordPage() {
  const { t } = useTranslation(['common']);
  const { showToast } = useToast();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.trim()) {
      showToast('error', 'Error', 'Please enter your email address.');
      return;
    }

    try {
      setIsSubmitting(true);
      await AuthControllerService.forgotPassword({ email: email.trim() });
      setIsSubmitted(true);
      showToast('success', 'Success', 'If the email exists, a password reset link has been sent.');
    } catch (error: any) {
      console.error('Password reset request failed:', error);
      showToast('error', 'Error', 'Failed to send password reset email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    window.location.href = '/login';
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              {t('auth.checkEmail')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {t('auth.resetEmailSent')}
            </p>
          </div>
          
          <div className="text-center">
            <ButtonComponent
              cssClass="e-primary"
              onClick={handleBackToLogin}
            >
              {t('auth.backToLogin')}
            </ButtonComponent>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('auth.forgotPassword')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('auth.forgotPasswordDescription')}
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-600">
              {t('auth.email')}
            </label>
            <TextBoxComponent
              type="email"
              floatLabelType="Never"
              cssClass="e-outline w-full"
              placeholder={t('auth.email_placeholder')}
              value={email}
              onChange={(e: any) => setEmail(e.value)}
            />
          </div>

          <ButtonComponent
            cssClass="e-primary w-full"
            type="submit"
            disabled={isSubmitting || !email.trim()}
          >
            {isSubmitting ? t('common.sending') : t('auth.sendResetLink')}
          </ButtonComponent>

          <div className="text-center">
            <button
              type="button"
              className="text-sm text-blue-600 hover:text-blue-500"
              onClick={handleBackToLogin}
            >
              {t('auth.backToLogin')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 